import { ReactElement, memo, useEffect, useState, useCallback } from 'react';

import CardPayment from '@/shared/images/icons/CardPayment';
import CardPaymentDark from '@/shared/images/icons/CardPaymentDark';
import { useThemeContext } from '@/common/app/contexts/ThemeContext';
import * as S from './BillingInfo.styles';
import { TBillingPosition, TSetPaymentMethod } from './BillingInfo.types';

import dynamic from 'next/dynamic';
import { StripeComponent } from './Stripe';
import { useCartContext } from '@/entities/Cart/app/CartContext';
import { useCheckoutContext } from '@/common/app/contexts/CheckoutContext';
import { useContactInfo } from '@/entities/Forms/contactInfo';
import { fetchConfig } from '@/entities/Checkout/service/fetchConfig';
import { QuickPayComponent } from './QuickPay';
import { trackPaymentInfo } from '@/shared/Analytics/GA4';
import { TPaymentMethod } from '../../domain/CheckoutPage.domain';
import { getAnalyticsDataForTrackViewCart } from '@/shared/Analytics/GA4/helpers';

const BillingInfoBraintree = dynamic(
    () => {
        return import('../Braintree');
    },
    { suspense: true, ssr: false }
);

const DynamicFluidPayForm = dynamic(
    () => {
        return import('./FluidPayContainer');
    },
    { suspense: true, ssr: false }
);

const getPaymentGateway = async (setPaymentMethod: TSetPaymentMethod) => {
    const { payment_gateway, enable_paypal_gateway } = await fetchConfig();
    const enablePayPal = !!+enable_paypal_gateway;

    if (payment_gateway === 'braintree' || !payment_gateway) {
        setPaymentMethod({
            type: 'braintree',
            name: 'Braintree',
            isStripe: false,
            isBraintree: true,
            isFluidPay: false,
            isPayPal: enablePayPal,
        });
    }
    if (payment_gateway === 'stripe') {
        setPaymentMethod({
            type: 'stripe',
            name: 'Stripe',
            isStripe: true,
            isBraintree: false,
            isFluidPay: false,
            isPayPal: enablePayPal,
        });
    }
    if (payment_gateway === 'fluidpay') {
        setPaymentMethod({
            type: 'fluidpay',
            name: 'FluidPay',
            isStripe: false,
            isBraintree: false,
            isFluidPay: true,
            isPayPal: enablePayPal,
        });
    }
};

const BillingInfo = ({ hideComponent, isContactInfoConfirmed }: TBillingPosition): ReactElement => {
    const { theme } = useThemeContext();
    const isDark = theme.name === 'dark';
    // TO DO add same infor for Stripe
    const { sessionId, userId, cartData, disableTimer } = useCartContext();
    const { isLoading, onSuccessfullyPayment } = useCheckoutContext();
    const checkSendSms = useContactInfo((s) => s.values.send_sms);
    const [paymentMethod, setPaymentMethod] = useState<TPaymentMethod>();
    const [paymentKey, setPaymentKey] = useState('Init Payment Key');

    const reloadPaymentForm = () => {
        setPaymentKey('Updated Payment Key');
    };

    const onChoosePaymentType = useCallback<EventListener>(
        (event) => {
            const element = event.target as HTMLElement;
            trackPaymentInfo({
                ...getAnalyticsDataForTrackViewCart(cartData),
                // eslint-disable-next-line sonarjs/cognitive-complexity
                payment_type: element.innerText === 'Card' ? 'Credit Card' : element.innerText,
            });
        },
        [cartData]
    );

    useEffect(() => {
        getPaymentGateway(setPaymentMethod);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <S.BillingInfoContainer hide={hideComponent} key={paymentKey}>
            <S.BillingInfoHeader isPaypal={paymentMethod?.isPayPal}>
                <S.BillingInfoTitle>
                    {isDark ? <CardPaymentDark /> : <CardPayment />}
                    {`Payment secured by ${paymentMethod?.name || ''}`}
                </S.BillingInfoTitle>
                {/* <PaymentBlock isPaypal={paymentMethod?.isPayPal} /> */}
            </S.BillingInfoHeader>
            <S.Title>Choose your payment method</S.Title>
            <S.BillingInfoBody data-test-id="Billing_info">
                {paymentMethod?.isStripe && (
                    <>
                        <QuickPayComponent
                            userId={userId}
                            sessionId={sessionId}
                            sendSms={checkSendSms}
                            checkoutLoading={isLoading}
                            onSuccessfullyPayment={onSuccessfullyPayment}
                            stopTimer={disableTimer}
                        />
                        <StripeComponent
                            userId={userId}
                            sessionId={sessionId}
                            sendSms={checkSendSms}
                            totalAmount={cartData.total}
                            checkoutLoading={isLoading}
                            onSuccessfullyPayment={onSuccessfullyPayment}
                            stopTimer={disableTimer}
                            showPayPal={paymentMethod.isPayPal}
                        />
                    </>
                )}
                {paymentMethod?.isBraintree && (
                    <BillingInfoBraintree
                        userId={userId}
                        sessionId={sessionId}
                        sendSms={checkSendSms}
                        totalAmount={cartData.total}
                        checkoutLoading={isLoading}
                        onChoosePaymentType={onChoosePaymentType}
                        onSuccessfullyPayment={onSuccessfullyPayment}
                        stopTimer={disableTimer}
                    />
                )}
                {paymentMethod?.isFluidPay && (
                    <DynamicFluidPayForm
                        userId={userId}
                        sessionId={sessionId}
                        sendSms={checkSendSms}
                        totalAmount={cartData.total}
                        checkoutLoading={isLoading}
                        onSuccessfullyPayment={onSuccessfullyPayment}
                        stopTimer={disableTimer}
                        isDisabledSubmit={!isContactInfoConfirmed}
                        reloadPaymentForm={reloadPaymentForm}
                        showPayPal={paymentMethod.isPayPal}
                    />
                )}
                {!!cartData.total && (
                    <S.TotalText>
                        <b>${cartData.total.toFixed(2)}</b> will be due today, which guarantees best
                        price and ticket availability.
                    </S.TotalText>
                )}
            </S.BillingInfoBody>
        </S.BillingInfoContainer>
    );
};

export { BillingInfo };
export default memo(BillingInfo);
